import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchCustomer, submitIban, CustomerInfo } from './api';
import { isValidIban, isValidBic } from './validators';
import Select from 'react-select';
import { COUNTRIES, Country, countryMode } from './sepa';
import ConfirmScreen from './ConfirmScreen';
import InvalidLink from './InvalidLink';

export default function IbanForm() {
    const { token } = useParams<{ token: string }>();
    const [customer, setCustomer] = useState<CustomerInfo | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [accountHolder, setAccountHolder] = useState('');
    const [bankCountry, setBankCountry] = useState('');
    const [iban, setIban] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [bic, setBic] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
    const [generalError, setGeneralError] = useState<string | null>(null);
    const [done, setDone] = useState<{ iban_last5: string } | null>(null);

    useEffect(() => {
        if (!token) return;
        fetchCustomer(token).then(res => {
            if (res.ok) {
                setCustomer(res.data);
                // Prefill dei campi non sensibili quanto l'IBAN completo.
                setAccountHolder(res.data.account_holder_name || '');
                setBankCountry(res.data.bank_country || '');
                setAccountNumber(res.data.account_number || '');
                setBic(res.data.swift || '');
            } else {
                setLoadError(res.message);
            }
        });
    }, [token]);

    // Quale campo mostrare dipende dal PAESE selezionato, non dai dati inseriti.
    // (classifyCircuit resterebbe 'missing' a campi vuoti, mostrando sempre l'IBAN.)
    // SEPA → IBAN; extra-SEPA → numero conto + BIC; group_c → bloccato.
    const mode = countryMode(bankCountry);
    // Il BIC è obbligatorio per i pagamenti extra-SEPA (coerente con il campo mostrato).
    const bicRequired = mode === 'extra_sepa';

    // Se il circuito è extra-SEPA il campo IBAN è nascosto. NON azzeriamo lo
    // stato `iban`: cancellare l'input mentre l'utente lo digita/incolla crea
    // l'impressione di un campo che "non si valorizza". L'IBAN viene invece
    // escluso al momento dell'invio quando il campo non è visibile (vedi submit),
    // così il valore digitato non va perso se l'utente cambia idea sul paese.

    // Opzioni per react-select. code = value, label bilingue.
    const countryOptions = useMemo<Country[]>(() => COUNTRIES, []);
    const selectedCountry = useMemo(
        () => countryOptions.find(c => c.code === bankCountry) ?? null,
        [countryOptions, bankCountry]
    );

    if (!token) return <InvalidLink reason="Token mancante." />;
    if (loadError) return <InvalidLink reason={loadError} />;
    if (done && customer) return <ConfirmScreen customerName={[customer.first_name, customer.last_name].filter(Boolean).join(' ')} ibanLast5={done.iban_last5} />;
    if (!customer) return <div className="bank-loading">Caricamento... / Loading...</div>;

    const fullName = [customer.first_name, customer.last_name].filter(Boolean).join(' ');

    const ibanFilled = iban.trim().length > 0;
    const ibanValid = !ibanFilled || isValidIban(iban);
    const bicFilled = bic.trim().length > 0;
    const bicValid = !bicFilled || isValidBic(bic);
    const nameValid = accountHolder.trim().length > 0 && accountHolder.length <= 255;
    const countrySelected = bankCountry.trim().length > 0;

    // Regole di completezza coerenti con il campo mostrato (mode = funzione del paese).
    // Il backend ri-valida comunque.
    let fieldsComplete = false;
    if (mode === 'sepa') {
        fieldsComplete = isValidIban(iban);
    } else if (mode === 'extra_sepa') {
        // Per extra-SEPA l'IBAN non è mostrato: serve numero conto + BIC.
        fieldsComplete = accountNumber.trim().length > 0 && bicFilled;
    }

    const isBlocked = mode === 'group_c';
    const canSubmit =
        nameValid && countrySelected && ibanValid && bicValid && fieldsComplete && !isBlocked && !submitting;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setServerErrors({});
        setGeneralError(null);
        // L'IBAN si invia solo quando è il campo mostrato (paese SEPA).
        // Per extra-SEPA non inviamo l'IBAN anche se nello stato c'è un residuo.
        const ibanToSend = mode === 'sepa' ? iban.trim() : '';
        const res = await submitIban(token, {
            account_holder_name: accountHolder.trim(),
            iban: ibanToSend,
            bic: bic.trim(),
            bank_country: bankCountry,
            account_number: accountNumber.trim(),
        });
        setSubmitting(false);
        if (res.ok) {
            setDone({ iban_last5: res.iban_last5 });
        } else if (res.errors && Object.keys(res.errors).length > 0) {
            setServerErrors(res.errors);
            setGeneralError(null);
        } else {
            setServerErrors({});
            setGeneralError(res.message || 'Errore durante il salvataggio.');
        }
    };

    return (
        <div className="bank-card">
            <header className="bank-header">
                <h1>{fullName || 'Dati bancari / Bank details'}</h1>
                <p className="bank-subtitle">
                    Inserisci i tuoi dati bancari per ricevere i rimborsi dovuti da <strong>CX</strong>.
                    <br />
                    <em>Submit your bank details to receive the refunds due from <strong>CX</strong>.</em>
                </p>
            </header>

            {customer.has_iban && customer.iban_last5 && (
                <div className="bank-banner-info">
                    Hai già un IBAN salvato che termina con <code>{customer.iban_last5}</code>. Compila i campi e salva per aggiornarlo.
                    <br />
                    <em>You already have an IBAN saved ending in <code>{customer.iban_last5}</code>. Fill the form and save to update it.</em>
                </div>
            )}

            {generalError && (
                <div className="bank-banner-error">
                    {generalError}
                </div>
            )}

            <form onSubmit={submit} className="bank-form">
                <label>
                    <span>Intestatario conto / Account holder *</span>
                    <input
                        type="text"
                        value={accountHolder}
                        onChange={e => setAccountHolder(e.target.value)}
                        maxLength={255}
                        required
                    />
                    {serverErrors.account_holder_name && <small className="bank-error">{serverErrors.account_holder_name}</small>}
                </label>

                <label>
                    <span>Nazione della banca / Bank country *</span>
                    <Select<Country>
                        classNamePrefix="bank-select"
                        options={countryOptions}
                        value={selectedCountry}
                        onChange={opt => setBankCountry(opt ? opt.code : '')}
                        getOptionValue={c => c.code}
                        getOptionLabel={c => c.label}
                        placeholder="— Seleziona / Select —"
                        noOptionsMessage={() => 'Nessun paese / No country'}
                        isClearable
                        isSearchable
                        aria-label="Nazione della banca / Bank country"
                    />
                    <small className="bank-hint">
                        Determina come inviare il rimborso (SEPA / estero).
                        <br />
                        <em>Determines how the refund is sent (SEPA / international).</em>
                    </small>
                    {serverErrors.bank_country && <small className="bank-error">{serverErrors.bank_country}</small>}
                </label>

                {isBlocked && (
                    <div className="bank-banner-error">
                        Per questo paese il rimborso non può essere gestito automaticamente. Contatta <strong>CX</strong> per assistenza.
                        <br />
                        <em>For this country the refund cannot be processed automatically. Contact <strong>CX</strong> for support.</em>
                    </div>
                )}

                {!isBlocked && (
                    <>
                        {mode === 'sepa' && (
                            <label>
                                <span>IBAN *</span>
                                <input
                                    type="text"
                                    value={iban}
                                    onChange={e => setIban(e.target.value)}
                                    autoCapitalize="characters"
                                    spellCheck={false}
                                />
                                {ibanFilled && !ibanValid && (
                                    <small className="bank-error">
                                        IBAN non valido o paese non supportato. Per assistenza, contatta CX.
                                        <br />
                                        <em>Invalid IBAN or country not supported. Contact CX for support.</em>
                                    </small>
                                )}
                                {serverErrors.iban && <small className="bank-error">{serverErrors.iban}</small>}
                            </label>
                        )}

                        {mode === 'extra_sepa' && (
                            <label>
                                <span>Numero conto / Account number *</span>
                                <input
                                    type="text"
                                    value={accountNumber}
                                    onChange={e => setAccountNumber(e.target.value)}
                                    maxLength={255}
                                    spellCheck={false}
                                />
                                <small className="bank-hint">
                                    Per banche estere senza IBAN (es. USA, Canada).
                                    <br />
                                    <em>For foreign banks without IBAN (e.g. USA, Canada).</em>
                                </small>
                                {serverErrors.account_number && <small className="bank-error">{serverErrors.account_number}</small>}
                            </label>
                        )}

                        {(mode === 'sepa' || mode === 'extra_sepa') && (
                            <label>
                                <span>BIC / SWIFT {bicRequired ? '*' : '(opzionale / optional)'}</span>
                                <input
                                    type="text"
                                    value={bic}
                                    onChange={e => setBic(e.target.value)}
                                    autoCapitalize="characters"
                                    spellCheck={false}
                                    required={bicRequired}
                                />
                                {bicFilled && !bicValid && <small className="bank-error">BIC non valido / Invalid BIC</small>}
                                {bicRequired && (
                                    <small className="bank-hint">
                                        Obbligatorio per pagamenti extra-SEPA.
                                        <br />
                                        <em>Required for non-SEPA payments.</em>
                                    </small>
                                )}
                                {serverErrors.bic && <small className="bank-error">{serverErrors.bic}</small>}
                            </label>
                        )}
                    </>
                )}

                <button type="submit" disabled={!canSubmit}>
                    {submitting ? 'Salvataggio... / Saving...' : 'Salva / Save'}
                </button>
            </form>
        </div>
    );
}
